/**
 * Chart data validation and performance utilities
 */
import _ from 'lodash';

export interface DataSizeConfig {
  /** Maximum recommended data points for optimal performance */
  optimal: number;
  /** Maximum data points before showing warning */
  warning: number;
  /** Maximum data points before preventing render */
  maximum: number;
}

export interface ChartDataValidation {
  isValid: boolean;
  dataSize: number;
  level: 'optimal' | 'warning' | 'critical' | 'blocked';
  message?: string;
  recommendation?: string;
}

// Default size limits for different chart types
export const CHART_SIZE_LIMITS: Record<string, DataSizeConfig> = {
  pie: {
    optimal: 10,
    warning: 20,
    maximum: 50,
  },
  bar: {
    optimal: 100,
    warning: 500,
    maximum: 2000,
  },
  heatmap: {
    optimal: 1000,
    warning: 5000,
    maximum: 20000,
  },
  line: {
    optimal: 500,
    warning: 2000,
    maximum: 10000,
  },
  scatter: {
    optimal: 1000,
    warning: 5000,
    maximum: 25000,
  },
  default: {
    optimal: 500,
    warning: 2000,
    maximum: 10000,
  },
};

/**
 * Calculate total data points in various data structures
 */
export const calculateDataSize = (data: any): number => {
  if (!data) return 0;

  // Array of objects with nested data (like HeatMapSerie)
  if (Array.isArray(data)) {
    return data.reduce((total, item) => {
      if (item && typeof item === 'object') {
        // Handle HeatMapSerie structure { id: string, data: Array }
        if (item.data && Array.isArray(item.data)) {
          return total + item.data.length;
        }
        // Handle flat array of data points
        return total + 1;
      }
      return total + 1;
    }, 0);
  }

  // Single object
  if (typeof data === 'object') {
    return 1;
  }

  return 0;
};

/**
 * Validate chart data size against limits
 */
export const validateChartData = (
  data: any,
  chartType: string = 'default',
  customLimits?: Partial<DataSizeConfig>,
): ChartDataValidation => {
  const dataSize = calculateDataSize(data);
  const limits = {
    ...(CHART_SIZE_LIMITS[chartType] || CHART_SIZE_LIMITS.default),
    ...customLimits,
  };

  if (dataSize === 0) {
    return {
      isValid: false,
      dataSize: 0,
      level: 'blocked',
      message: 'No data available',
      recommendation: 'Check data source and filters',
    };
  }

  if (dataSize > limits.maximum) {
    return {
      isValid: false,
      dataSize,
      level: 'blocked',
      message: `Dataset too large (${dataSize.toLocaleString()} data points)`,
      recommendation: `Reduce to under ${limits.maximum.toLocaleString()} data points using filters or pagination`,
    };
  }

  if (dataSize > limits.warning) {
    return {
      isValid: true,
      dataSize,
      level: 'critical',
      message: `Large dataset may cause performance issues (${dataSize.toLocaleString()} data points)`,
      recommendation: `Consider reducing to under ${limits.warning.toLocaleString()} data points for better performance`,
    };
  }

  if (dataSize > limits.optimal) {
    return {
      isValid: true,
      dataSize,
      level: 'warning',
      message: `Dataset larger than optimal (${dataSize.toLocaleString()} data points)`,
      recommendation: `For best performance, reduce to under ${limits.optimal.toLocaleString()} data points`,
    };
  }

  return {
    isValid: true,
    dataSize,
    level: 'optimal',
  };
};

/**
 * Chunk large datasets for progressive rendering
 */
export const chunkDataForRendering = <T>(
  data: T[],
  chunkSize: number = 1000,
): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < data.length; i += chunkSize) {
    chunks.push(data.slice(i, i + chunkSize));
  }
  return chunks;
};

/**
 * Sample data to reduce size while maintaining distribution
 */
export const sampleData = <T>(
  data: T[],
  targetSize: number,
  strategy: 'random' | 'systematic' | 'first' | 'last' = 'systematic',
): T[] => {
  if (data.length <= targetSize) return data;

  switch (strategy) {
    case 'random':
      return _.sampleSize(data, targetSize);

    case 'first':
      return data.slice(0, targetSize);

    case 'last':
      return data.slice(-targetSize);

    case 'systematic':
    default:
      const step = Math.floor(data.length / targetSize);
      return data.filter((_, index) => index % step === 0).slice(0, targetSize);
  }
};

/**
 * Performance monitoring for chart rendering
 */
export class ChartPerformanceMonitor {
  private startTime: number = 0;
  private dataSize: number = 0;

  start(dataSize: number) {
    this.startTime = performance.now();
    this.dataSize = dataSize;
  }

  end(chartType: string) {
    const renderTime = performance.now() - this.startTime;

    // Log performance metrics
    console.log(`Chart Performance [${chartType}]:`, {
      dataSize: this.dataSize,
      renderTime: `${renderTime.toFixed(2)}ms`,
      pointsPerMs: (this.dataSize / renderTime).toFixed(2),
    });

    // Warn about slow rendering
    if (renderTime > 1000) {
      console.warn(
        `Slow chart render: ${renderTime.toFixed(2)}ms for ${this.dataSize} data points`,
      );
    }

    return { renderTime, dataSize: this.dataSize };
  }
}
